// Pilot activation (business plan §7.1 and §9.2): of the cars a workshop put
// on its board during weeks 3 to 8 of its pilot, how many went through a
// signed Ingreso in the app. Goal: at least 80 %. The superadmin cannot read
// a workshop's orders or inspections (firestore.rules), so the callable
// `activacionPilotos` returns only totals per workshop, never a plate.
import { z } from 'zod';
import { esDiaIso, sumarDias } from './comercial.ts';
import { diaChile } from './metricas.ts';

/** Window of the metric, in days of the pilot (day 1 = `pilotoInicio`): weeks 3 to 8. */
export const VENTANA_ACTIVACION = { desdeDia: 15, hastaDia: 56 } as const;

/** Activation goal (autosConIngreso / autosTablero). */
export const META_ACTIVACION = 0.8;

/**
 * First instant (ms) of day 'AAAA-MM-DD' in Chile (America/Santiago). Chile
 * switches offset at midnight, so on those days 00:00 may not exist (or the
 * day starts at 00:00 of the other offset): the earliest of the two
 * candidates that already falls on that day is its real start.
 */
export function inicioDiaChile(dia: string): number {
  if (!esDiaIso(dia)) throw new Error(`Día inválido: ${dia}`);
  const candidatos = ['-03:00', '-04:00']
    .map((off) => Date.parse(`${dia}T00:00:00${off}`))
    .filter((ms) => diaChile(ms) === dia);
  if (!candidatos.length) throw new Error(`Sin inicio en Chile: ${dia}`);
  return Math.min(...candidatos);
}

/**
 * Instants [desdeMs, hastaMs) of the activation window of a pilot that
 * started on `pilotoInicio` (Chile days 15 to 56, both included), cut at
 * `ahoraMs`. null while day 15 has not started (nothing to measure yet).
 */
export function ventanaActivacion(
  pilotoInicio: string,
  ahoraMs: number,
): { desdeMs: number; hastaMs: number } | null {
  const desdeMs = inicioDiaChile(sumarDias(pilotoInicio, VENTANA_ACTIVACION.desdeDia - 1));
  const finMs = inicioDiaChile(sumarDias(pilotoInicio, VENTANA_ACTIVACION.hastaDia));
  const hastaMs = Math.min(finMs, ahoraMs);
  return hastaMs > desdeMs ? { desdeMs, hastaMs } : null;
}

/** Input of `activacionPilotos`: nothing (the client SDK sends null). */
export const entradaActivacionPilotos = z.strictObject({});

const total = z.number().int().nonnegative();

/** One workshop in pilot ('piloto' ficha with `pilotoInicio`). Totals only. */
export const activacionPiloto = z.strictObject({
  tallerId: z.string(),
  /** Board orders created inside the window. */
  autosTablero: total,
  /** Of those, the ones whose inspection is a signed Ingreso (tipo 'recepcion'). */
  autosConIngreso: total,
  /** Server instant of the workshop's latest signed inspection (any type), or null. */
  ultimaInspeccionMs: z.number().int().nonnegative().nullable(),
});
export type ActivacionPiloto = z.infer<typeof activacionPiloto>;

/** Answer of `activacionPilotos`, sorted by tallerId. */
export const respuestaActivacionPilotos = z.array(activacionPiloto);
export type RespuestaActivacionPilotos = z.infer<typeof respuestaActivacionPilotos>;
